/**
 * SS-07 (#1705): トライアル中ワークスペースの介入リスト。
 *
 * 「15分相談しますか？」と声をかけるべき会社を 1 画面で把握する
 * （`docs/growth/self-serve-strategy.md` §4、`docs/growth/growth-canon.md` §29）。
 * 同じ条件で毎朝 09:00 JST に運営 Slack へもダイジェストが届く（`apps/api` の cron）。
 *
 * 守るライン:
 *   - PII を表示しない（会社名 / 個人 email / 氏名は API レスポンスにも含まない）
 *   - 手動フラグのメモは Founder 用。運営面から外へ出さない
 *
 * PC 専用（min-width: 1024px、`WorkspaceListPage` と同じ）。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TrialFlagKey } from '../../lib/trials-api';
import { fetchTrials, setTrialFlag } from '../../lib/trials-api';
import { TrialTable } from './TrialTable';

export interface TrialListPageProps {
  apiBaseUrl: string;
}

const QUERY_KEY = ['admin-ops-trials'] as const;

export function TrialListPage({ apiBaseUrl }: TrialListPageProps) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => fetchTrials(apiBaseUrl),
  });

  const flagMutation = useMutation({
    mutationFn: ({
      workspaceId,
      flagKey,
      enabled,
    }: {
      workspaceId: string;
      flagKey: TrialFlagKey;
      enabled: boolean;
    }) => setTrialFlag(apiBaseUrl, workspaceId, flagKey, enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="hidden lg:block">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <header className="mb-6">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              ALYS Internal / admin-ops
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">トライアル中の介入リスト</h1>
            <p className="mt-2 text-sm text-slate-600">
              SS-07: 「15分相談しますか？」を提案する候補。介入理由が付いた会社が上に来ます。
              同じ条件のダイジェストが毎朝 09:00 に運営 Slack へも届きます。 PII
              は表示されません（集約値のみ）。
            </p>
          </header>

          <section>
            {query.isLoading && <p className="text-sm text-slate-500">読み込み中…</p>}
            {query.isError && (
              <p className="text-sm text-rose-600">
                API 取得に失敗しました: {(query.error as Error).message}
              </p>
            )}
            {query.data && !query.data.configured && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                {query.data.note ?? 'admin-ops-api のトライアル一覧が未配線です。'}
              </p>
            )}
            {query.data?.configured && (
              <>
                {flagMutation.isError && (
                  <p className="mb-3 text-sm text-rose-600">
                    フラグの更新に失敗しました: {(flagMutation.error as Error).message}
                  </p>
                )}
                <TrialTable
                  rows={query.data.rows}
                  onFlagChange={(workspaceId, flagKey, enabled) =>
                    flagMutation.mutate({ workspaceId, flagKey, enabled })
                  }
                />
              </>
            )}
          </section>
        </div>
      </div>

      <div className="block lg:hidden">
        <div className="mx-auto max-w-md px-6 py-12 text-center">
          <h1 className="text-lg font-semibold text-slate-900">PC 専用画面</h1>
          <p className="mt-3 text-sm text-slate-600">
            admin-ops は最小 1024px の画面幅でのみ動作します（情報密度が高いため）。 PC
            ブラウザでアクセスしてください。
          </p>
        </div>
      </div>
    </main>
  );
}
